import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { journeyApi } from '../../api/journeyApi';
import { memberDisplayName } from '../member-display/memberDisplayModel';
import {
  canLeaveJourney,
  canRemoveJourneyMember,
  canTransferJourneyOwnership,
  classifyMembershipMutationError,
  eligibleOwnershipTransferTargets,
  leaveBlockedByLifecycleMessage,
  membershipMutationErrorMessage,
  ownerLeaveBlockedMessage,
} from './journeyMembershipModel';

const MEMBERSHIP_STATUS_LABELS = {
  ACTIVE: '加入中',
  LEFT: '已離開',
  REMOVED: '已移除',
};

function membershipStatusLabel(status) {
  return MEMBERSHIP_STATUS_LABELS[status] || status || '';
}

/**
 * AUTH58 People：離開／移除／轉移擁有權（ONLINE_ONLY）。
 */
export default function JourneyPeopleMembershipPanel({
  journey,
  viewerMemberId,
  offlineCached = false,
  disabled = false,
  memberSummaries,
  onMembersRefresh,
}) {
  const navigate = useNavigate();
  const busy = useRef(false);
  const transferSelectId = useId();
  const confirmTitleId = useId();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [transferTargetId, setTransferTargetId] = useState('');

  if (!journey) return null;

  const actorRole = journey.requesterRole || '';
  const actorStatus = (journey.members || []).find(
    (row) => String(row.memberId) === String(viewerMemberId),
  )?.membershipStatus || '';
  const lifecycleStatus = journey.lifecycleStatus || '';
  const rowVersion = journey.rowVersion;

  const leaveAllowed = canLeaveJourney({
    actorRole,
    actorStatus,
    lifecycleStatus,
    offlineCached,
  });
  const ownerBlocked = ownerLeaveBlockedMessage({ actorRole, actorStatus });
  const lifecycleBlocked = leaveBlockedByLifecycleMessage(lifecycleStatus);
  const transferAllowed = canTransferJourneyOwnership({
    actorRole,
    actorStatus,
    offlineCached,
  });
  const transferTargets = transferAllowed
    ? eligibleOwnershipTransferTargets(journey.members, journey.ownerMemberId)
    : [];

  function closeConfirm() {
    if (busy.current) return;
    setConfirm(null);
    setError('');
  }

  async function runRefresh() {
    if (onMembersRefresh) await onMembersRefresh();
  }

  async function submitConfirm() {
    if (!confirm || busy.current || offlineCached || disabled) return;
    if (rowVersion == null) {
      setError('缺少旅程版本，請重新載入後再試。');
      return;
    }
    busy.current = true;
    setPending(true);
    setError('');
    try {
      if (confirm.type === 'leave') {
        await journeyApi.leave(journey.journeyId, {
          expectedRowVersion: rowVersion,
          confirm: true,
        });
        setConfirm(null);
        navigate('/journeys', { replace: true });
        return;
      }
      if (confirm.type === 'remove') {
        await journeyApi.removeMember(journey.journeyId, confirm.targetMemberId, {
          expectedRowVersion: rowVersion,
          confirm: true,
        });
        setConfirm(null);
        await runRefresh();
        return;
      }
      if (confirm.type === 'transfer') {
        await journeyApi.transferOwnership(journey.journeyId, {
          targetMemberId: confirm.targetMemberId,
          expectedRowVersion: rowVersion,
        });
        setConfirm(null);
        setTransferTargetId('');
        await runRefresh();
      }
    } catch (cause) {
      const kind = classifyMembershipMutationError(cause);
      const message = membershipMutationErrorMessage(kind, cause?.raw?.message || cause?.message);
      setError(message);
      if (kind === 'conflict' || kind === 'invalid-lifecycle') {
        setConfirm(null);
        await runRefresh();
      }
    } finally {
      busy.current = false;
      setPending(false);
    }
  }

  function openLeaveConfirm() {
    if (!leaveAllowed || busy.current || offlineCached || disabled) return;
    setError('');
    setConfirm({ type: 'leave' });
  }

  function openRemoveConfirm(member) {
    const allowed = canRemoveJourneyMember({
      actorRole,
      actorStatus,
      actorMemberId: viewerMemberId,
      targetRole: member.role,
      targetStatus: member.membershipStatus,
      targetMemberId: member.memberId,
      lifecycleStatus,
      offlineCached,
    });
    if (!allowed || busy.current || offlineCached || disabled) return;
    setError('');
    setConfirm({
      type: 'remove',
      targetMemberId: member.memberId,
      targetLabel: memberDisplayName(String(member.memberId), memberSummaries),
      targetRole: member.roleLabel || member.role,
    });
  }

  function openTransferConfirm() {
    if (!transferAllowed || busy.current || offlineCached || disabled) return;
    if (!transferTargetId) {
      setError('請先選擇要移交擁有權的旅伴。');
      return;
    }
    const target = transferTargets.find(
      (row) => String(row.memberId) === String(transferTargetId),
    );
    if (!target) {
      setError('所選旅伴無法接收擁有權。');
      return;
    }
    setError('');
    setConfirm({
      type: 'transfer',
      targetMemberId: target.memberId,
      targetLabel: memberDisplayName(String(target.memberId), memberSummaries),
    });
  }

  const confirmCopy = confirm?.type === 'leave'
    ? {
      title: '確認離開旅程',
      body: '離開後你將無法再存取此旅程的私人工作區。確定要離開嗎？',
      confirmLabel: '確認離開',
      danger: true,
    }
    : confirm?.type === 'remove'
      ? {
        title: '確認移除旅伴',
        body: `確定要移除「${confirm.targetLabel}」（${confirm.targetRole}）嗎？對方將無法再存取此旅程工作區。`,
        confirmLabel: '確認移除',
        danger: true,
      }
      : confirm?.type === 'transfer'
        ? {
          title: '確認轉移擁有權',
          body: `「${confirm.targetLabel}」將成為 Owner，你會變為 COORDINATOR。此操作不會自動讓你離開旅程。`,
          confirmLabel: '確認轉移',
          danger: false,
        }
        : null;

  return (
    <div className="journey-people-membership" data-testid="people-membership-panel">
      {!offlineCached ? (
        <div className="journey-people-membership__self">
          {leaveAllowed ? (
            <button
              type="button"
              className="journey-workspace__lifecycle-danger"
              data-testid="people-leave-button"
              disabled={disabled || pending}
              onClick={openLeaveConfirm}
            >
              離開旅程
            </button>
          ) : null}
          {ownerBlocked ? (
            <p className="journey-people-membership__hint" role="status" data-testid="people-owner-leave-hint">
              {ownerBlocked}
            </p>
          ) : null}
          {!leaveAllowed && !ownerBlocked && lifecycleBlocked ? (
            <p className="journey-people-membership__hint" role="status">{lifecycleBlocked}</p>
          ) : null}
        </div>
      ) : null}

      {transferAllowed && !offlineCached ? (
        <div className="journey-people-membership__transfer" data-testid="people-transfer-panel">
          <label htmlFor={transferSelectId}>轉移擁有權給</label>
          <select
            id={transferSelectId}
            data-testid="people-transfer-select"
            value={transferTargetId}
            disabled={disabled || pending || transferTargets.length === 0}
            onChange={(event) => {
              setTransferTargetId(event.target.value);
              setError('');
            }}
          >
            <option value="">選擇可接收的旅伴</option>
            {transferTargets.map((member) => {
              const id = String(member.memberId);
              const name = memberDisplayName(id, memberSummaries);
              return (
                <option key={id} value={id}>
                  {name}（{member.roleLabel || member.role}）
                </option>
              );
            })}
          </select>
          <button
            type="button"
            className="journey-workspace__lifecycle-primary"
            data-testid="people-transfer-button"
            disabled={disabled || pending || !transferTargetId || transferTargets.length === 0}
            onClick={openTransferConfirm}
          >
            轉移擁有權
          </button>
          {transferTargets.length === 0 ? (
            <p className="journey-people-membership__hint" role="status">
              目前沒有可接收擁有權的協調者或旅伴。
            </p>
          ) : null}
        </div>
      ) : null}

      {error && !confirm ? (
        <p className="journey-workspace__error" role="alert" data-testid="people-membership-error">
          {error}
        </p>
      ) : null}

      {journey.members.length === 0 ? (
        <p>這趟旅程尚未列出成員。</p>
      ) : (
        <ul className="journey-workspace__people">
          {journey.members.map((member) => {
            const memberId = String(member.memberId);
            const displayName = memberDisplayName(memberId, memberSummaries);
            const canRemove = canRemoveJourneyMember({
              actorRole,
              actorStatus,
              actorMemberId: viewerMemberId,
              targetRole: member.role,
              targetStatus: member.membershipStatus,
              targetMemberId: member.memberId,
              lifecycleStatus,
              offlineCached,
            });
            return (
              <li key={`${memberId}-${member.role}-${member.membershipStatus}`}>
                <div className="journey-people-membership__row">
                  <div className="journey-people-membership__identity">
                    <strong className="journey-workspace__people-name">{displayName}</strong>
                    <span>{member.roleLabel}</span>
                    <span>{membershipStatusLabel(member.membershipStatus)}</span>
                  </div>
                  {canRemove && !offlineCached ? (
                    <button
                      type="button"
                      className="journey-workspace__lifecycle-danger"
                      data-testid={`people-remove-${memberId}`}
                      disabled={disabled || pending}
                      onClick={() => openRemoveConfirm(member)}
                    >
                      移除
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {confirm && confirmCopy && !offlineCached ? (
        <div className="journey-workspace-lifecycle-confirm" role="presentation">
          <button
            type="button"
            className="journey-workspace-lifecycle-confirm__backdrop"
            aria-label="關閉確認"
            disabled={pending}
            onClick={closeConfirm}
          />
          <div
            className="journey-workspace-lifecycle-confirm__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={confirmTitleId}
          >
            <h3 id={confirmTitleId}>{confirmCopy.title}</h3>
            <p>{confirmCopy.body}</p>
            {error ? (
              <p className="journey-workspace__error" role="alert">{error}</p>
            ) : null}
            <div className="journey-workspace-lifecycle-confirm__actions">
              <button type="button" disabled={pending} onClick={closeConfirm}>
                返回
              </button>
              <button
                type="button"
                className={confirmCopy.danger
                  ? 'journey-workspace__lifecycle-danger'
                  : 'journey-workspace__lifecycle-primary'}
                data-testid="people-membership-confirm"
                disabled={pending}
                onClick={submitConfirm}
              >
                {pending ? '處理中…' : confirmCopy.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
